import { readStoredLanguage } from '@workingclock/i18n';
import { ThemeProvider } from '@workingclock/ui';
import { useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { SeoHead } from './components/seo/seo-head';
import { CookieConsentProvider } from './contexts/cookie-consent-context';
import { i18n } from './i18n';

/**
 * Pathless root route element: the app-wide provider stack that used to live in
 * App.tsx, minus the router (vite-react-ssg owns the router now). Rendered on
 * both the server (SSG pre-render) and the client, so every provider here must
 * be SSR-safe — they are: ThemeProvider/CookieConsentProvider touch the DOM
 * only inside effects, and the i18n factory falls back to English outside a
 * browser (see libs/i18n/src/index.ts).
 *
 * SeoHead sits inside I18nextProvider (it translates route titles) and inside
 * the router (it reads the active route + path), then `<Outlet />` renders the
 * matched page under the visual chrome (MarketingLayout, one level down).
 */
export function RootLayout() {
  // The tree pins the initial language to English so SSG output and the first
  // client render match (see src/i18n/index.ts). Once hydrated, switch to the
  // visitor's stored/browser language — this runs only in the browser, after
  // hydration, so it never desyncs the server/client initial paint.
  useEffect(() => {
    const preferred = readStoredLanguage();
    if (preferred !== i18n.language) {
      void i18n.changeLanguage(preferred);
    }
  }, []);

  return (
    <ThemeProvider>
      <I18nextProvider i18n={i18n}>
        <CookieConsentProvider>
          <SeoHead />
          <Outlet />
        </CookieConsentProvider>
      </I18nextProvider>
    </ThemeProvider>
  );
}
